import { Button } from '@folio/components/ui/button';
import { SearchableSelect } from '@folio/components/ui/searchable-select';
import { useToast } from '@folio/components/ui/use-toast';
import { cn } from '@folio/lib';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { documentsControllerSetDocumentCategory, ForUserError } from 'korint-sdk';
import { ArrowRightLeftIcon, Loader2Icon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import type { DocumentCustomCategorieMapping, DocumentViewerDocument } from './types';
import { useTranslation } from 'react-i18next';
import { parseAIClassificationMetadata } from './DocumentClassificationStatus';
import { isUnresolvedCategory } from '@folio/utils/document-categories';
import { CorrectionPopover } from './CorrectionPopover';

function findNextUnclassifiedDocument(
  documents: DocumentViewerDocument[],
  currentDocumentId: string,
): DocumentViewerDocument | null {
  const currentIndex = documents.findIndex(d => d.id === currentDocumentId);

  for (let i = currentIndex + 1; i < documents.length; i++) {
    const doc = documents[i];
    if (
      (doc.type === 'supporting_document' || doc.type === 'non_delegated') &&
      isUnresolvedCategory(doc.category)
    ) {
      return doc;
    }
  }

  return null;
}

interface DocumentClassificationSelectorProps {
  needsReview?: boolean;
  document: DocumentViewerDocument;
  categories: DocumentCustomCategorieMapping[];
  documents: DocumentViewerDocument[];
  onDocumentChange: (doc: DocumentViewerDocument) => void;
  onClose: () => void;
}

export function DocumentClassificationSelector({
  needsReview = false,
  document,
  categories,
  documents,
  onDocumentChange,
  onClose,
}: DocumentClassificationSelectorProps) {
  const { t } = useTranslation();

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const aiClassification = useMemo(() => {
    return parseAIClassificationMetadata(document.metadata.aiclassification);
  }, [document.metadata.aiclassification]);

  const currentCategory = useMemo(
    () => (document.category ? String(document.category) : undefined),
    [document.category],
  );

  const categoryOptions = useMemo(
    () =>
      categories.map(({ category }) => ({
        value: category,
        label: t(`documents.${category}`),
      })),
    [categories, t],
  );

  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(
    currentCategory ?? undefined,
  );

  useEffect(() => {
    setSelectedCategory(currentCategory ?? undefined);
  }, [currentCategory, document.id]);

  const { mutate: updateDocument, isPending } = useMutation({
    mutationFn: (categoryValue: string) => {
      return documentsControllerSetDocumentCategory(document.id, {
        category: categoryValue,
        // Gate on the metadata itself, not traceId — the backend stopped
        // writing traceId with the Dash0 migration (feedback is keyed on the
        // metadata's aiOperationId server-side).
        ...(aiClassification && {
          aiCorrection: {
            originalCategory:
              aiClassification.suggestedCategory ?? aiClassification.suggestedcategory ?? '',
          },
        }),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries();

      if (needsReview || !isUnresolvedCategory(currentCategory)) return;

      const nextUnclassified = findNextUnclassifiedDocument(documents, document.id);

      if (nextUnclassified) {
        onDocumentChange(nextUnclassified);
      } else {
        onClose();
      }
    },
    onError: (error: ForUserError) => {
      setSelectedCategory(currentCategory);
      if (error.userError) return;
      toast({
        title: error.userError ?? t('documents.classification.errorTitle'),
        description: error.userHelp ?? t('documents.classification.errorDescription'),
      });
    },
  });

  const canConfirmPendingReview =
    needsReview &&
    aiClassification?.status === 'pending_review' &&
    !!currentCategory &&
    !isUnresolvedCategory(currentCategory);

  return (
    <span className={cn('inline-flex items-center gap-1 flex-shrink-0', needsReview && 'w-full')}>
      <CorrectionPopover
        title={t('documentReview.correction.classificationTitle')}
        canSave={
          !!selectedCategory && (selectedCategory !== currentCategory || canConfirmPendingReview)
        }
        isPending={isPending}
        onOpenChange={open => open && setSelectedCategory(currentCategory)}
        withReason={false}
        onSave={() => selectedCategory && updateDocument(selectedCategory)}
        trigger={
          needsReview ? (
            <Button
              variant="ghost"
              className="h-9 w-full rounded text-amber-950 hover:bg-amber-100"
              aria-label={t('documentReview.reviewCategory')}
              aria-busy={isPending}
              disabled={isPending}
              type="button"
            >
              {isPending ? (
                <Loader2Icon className="h-3 w-3 animate-spin" />
              ) : (
                <ArrowRightLeftIcon className="h-3 w-3" />
              )}
              <span className="ml-2">{t('documentReview.reviewCategory')}</span>
            </Button>
          ) : undefined
        }
      >
        <SearchableSelect
          value={selectedCategory}
          options={categoryOptions}
          onChange={value => value && setSelectedCategory(value)}
          disabled={isPending}
          placeholder={t('documents.classification.selectPlaceholder')}
          searchable
        />
      </CorrectionPopover>
    </span>
  );
}
